import { useState, useRef, type ComponentType } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Popover,
    IconButton,
    Badge,
    Box,
    Typography,
    Button,
    Divider,
    Skeleton,
    type SvgIconProps,
    type SxProps,
    type Theme,
} from '@mui/material';
import NotificationsIcon from '@mui/icons-material/Notifications';
import SettingsIcon from '@mui/icons-material/Settings';
import { useNotifications } from './hooks/useNotifications';
import { AlertRow } from '../pages/markets/components/AlertRow';
import { TraderTradeNotificationRow } from './TraderTradeNotificationRow';
import { GateNotificationRow } from './GateNotificationRow';
import { hoverScrollbar } from '../styles/hoverScrollbar';
import { inHomeScreenApp, openInNewTab } from '../utils/newTab';

// The dot of the phone chrome sits inside the icon's top-right corner (MUI
// centres a badge on the corner), and stays put while it scales in and out.
const DOT_SX = {
    '& .MuiBadge-badge': {
        top: 0,
        right: 0,
        transform: 'none',
        '&.MuiBadge-invisible': { transform: 'scale(0)' },
    },
} as const;

interface NotificationBellProps {
    /** Draws this instead of the Material bell (the phone chrome has outline icons). */
    icon?: ComponentType<SvgIconProps>;
    /** A red dot while anything is unread, instead of the count. */
    dot?: boolean;
    /** Extra styles for the button. */
    sx?: SxProps<Theme>;
}

export function NotificationBell({
    icon: BellGlyph = NotificationsIcon,
    dot,
    sx,
}: NotificationBellProps = {}) {
    const anchorRef = useRef<HTMLButtonElement>(null);
    const [open, setOpen] = useState(false);
    const navigate = useNavigate();
    // Event and asset pages open in a new tab. In an iPhone Home Screen app a new
    // tab lands in Safari, signed out, and a page load would drop the read
    // receipt still on its way (Firestore keeps no offline cache here), so the
    // router moves the page instead.
    const openPage = (path: string) =>
        inHomeScreenApp() ? navigate(path) : openInNewTab(path);
    const {
        notifications,
        unreadCount,
        loading,
        hasMore,
        loadMore,
        markRead,
        markAllRead,
    } = useNotifications({ pageSize: 20 });

    return (
        <>
            <IconButton
                ref={anchorRef}
                onClick={() => setOpen(o => !o)}
                size="small"
                color="inherit"
                aria-label={`Notifications (${unreadCount} unread)`}
                sx={sx}
            >
                {dot ? (
                    <Badge
                        variant="dot"
                        color="error"
                        invisible={unreadCount === 0}
                        overlap="rectangular"
                        sx={DOT_SX}
                    >
                        <BellGlyph />
                    </Badge>
                ) : (
                    <Badge
                        badgeContent={unreadCount > 99 ? '99+' : unreadCount}
                        color="primary"
                        max={999}
                    >
                        <BellGlyph />
                    </Badge>
                )}
            </IconButton>

            <Popover
                open={open}
                anchorEl={anchorRef.current}
                onClose={() => setOpen(false)}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                slotProps={{
                    paper: {
                        sx: {
                            width: 380,
                            maxHeight: 520,
                            display: 'flex',
                            flexDirection: 'column',
                        },
                    },
                }}
            >
                {/* Header */}
                <Box
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        p: 1.5,
                    }}
                >
                    <Typography variant="body2" fontWeight={600}>
                        Notifications{' '}
                        {unreadCount > 0 && (
                            <span style={{ color: '#26a6ed' }}>
                                {unreadCount}
                            </span>
                        )}
                    </Typography>
                    <Box>
                        <Button
                            size="small"
                            disabled={unreadCount === 0}
                            onClick={markAllRead}
                            sx={{ fontSize: '0.7rem', mr: 0.5 }}
                        >
                            Mark all read
                        </Button>
                        <IconButton
                            size="small"
                            onClick={() => {
                                setOpen(false);
                                navigate('/settings/notifications');
                            }}
                        >
                            <SettingsIcon fontSize="small" />
                        </IconButton>
                    </Box>
                </Box>
                <Divider />

                {/* Notification list */}
                <Box sx={{ ...hoverScrollbar, flex: 1, overflowY: 'auto' }}>
                    {loading &&
                        Array.from({ length: 5 }).map((_, i) => (
                            <Box key={i} sx={{ p: 1 }}>
                                <Skeleton variant="rectangular" height={48} />
                            </Box>
                        ))}

                    {!loading && notifications.length === 0 && (
                        <Box
                            sx={{
                                p: 3,
                                textAlign: 'center',
                                color: 'text.secondary',
                            }}
                        >
                            <Typography variant="body2">
                                No notifications yet
                            </Typography>
                            <Typography variant="caption">
                                Visit an event and subscribe to start receiving
                                alerts.
                            </Typography>
                        </Box>
                    )}

                    {notifications.map(n => {
                        const isTrader = n.alert.alert_type === 'trader_trade';
                        const traderAddr = (
                            n.alert.context as
                                | { trader_address?: string }
                                | undefined
                        )?.trader_address;
                        const isGate = n.alert.alert_type === 'gate_request';
                        const gateCtx = n.alert.context as
                            | { account_id?: string; condition_id?: string }
                            | undefined;
                        const openTrader = async () => {
                            if (!n.read) await markRead(n.notif_id);
                            setOpen(false);
                            if (traderAddr)
                                navigate(`/markets/trader/${traderAddr}`);
                        };
                        return (
                            <Box
                                key={n.notif_id}
                                onClick={() => {
                                    // Event and asset pages open in a new tab. Start the
                                    // read receipt without waiting for it: a tab opened
                                    // after a network round trip can lose the click's user
                                    // activation and get blocked as a popup.
                                    if (!n.read) void markRead(n.notif_id);
                                    setOpen(false);
                                    if (isGate) {
                                        const params = new URLSearchParams();
                                        if (gateCtx?.account_id)
                                            params.set(
                                                'account',
                                                gateCtx.account_id
                                            );
                                        if (gateCtx?.condition_id)
                                            params.set(
                                                'market',
                                                gateCtx.condition_id
                                            );
                                        navigate(`/gates?${params.toString()}`);
                                    } else if (isTrader) {
                                        // Prefer the event view with the trader preselected; fall
                                        // back to the trader view when the producer couldn't
                                        // resolve the owning event (event_id null).
                                        if (n.alert.event_id && traderAddr)
                                            openPage(
                                                `/markets/event/${n.alert.event_id}?traders=${encodeURIComponent(traderAddr)}`
                                            );
                                        else if (n.alert.event_id)
                                            openPage(
                                                `/markets/event/${n.alert.event_id}`
                                            );
                                        else if (traderAddr)
                                            navigate(
                                                `/markets/trader/${traderAddr}`
                                            );
                                    } else if (
                                        n.alert.event_id?.startsWith(
                                            'pmsignal_'
                                        )
                                    ) {
                                        // pm-signal rides a synthetic event id; deep-link to the
                                        // asset page (not /markets/event/pmsignal_btc).
                                        const asset = n.alert.event_id
                                            .slice('pmsignal_'.length)
                                            .toUpperCase();
                                        openPage(
                                            `/markets/asset/cryptocurrency/${asset}`
                                        );
                                    } else {
                                        openPage(
                                            `/markets/event/${n.alert.event_id}`
                                        );
                                    }
                                }}
                                sx={{
                                    cursor: 'pointer',
                                    opacity: n.read ? 0.6 : 1,
                                }}
                            >
                                {isGate ? (
                                    <GateNotificationRow alert={n.alert} />
                                ) : isTrader ? (
                                    <TraderTradeNotificationRow
                                        alert={n.alert}
                                        onOpenTrader={openTrader}
                                    />
                                ) : (
                                    <AlertRow
                                        alert={n.alert}
                                        marketQuestion={n.event_title}
                                    />
                                )}
                            </Box>
                        );
                    })}
                </Box>

                {/* Load more footer */}
                {hasMore && !loading && (
                    <>
                        <Divider />
                        <Box sx={{ textAlign: 'center', p: 1 }}>
                            <Button size="small" onClick={loadMore}>
                                Load more
                            </Button>
                        </Box>
                    </>
                )}
            </Popover>
        </>
    );
}
